import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowLeft, BookOpen, ChevronDown, ChevronRight, ChevronUp, List, Download, Loader2 } from "lucide-react";
import { API } from "../App";
import { toast } from "sonner";

/**
 * Generic Guide viewer.
 * Fetches /api/guides/{slug}, renders with expand/collapse + table-of-contents.
 * Admin gets a Download (PDF) button via the canDownload flag from the API.
 *
 * Props:
 *   slug         — guide slug (e.g. "admin", "coach", "expert", "relationship", "life")
 *   token        — auth token for API calls
 *   onBack       — callback to leave the viewer
 *   canDownload  — optional override; if absent, derived from the API response
 */
export default function GuideViewer({ slug, token, onBack, canDownload }) {
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [showIndex, setShowIndex] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/guides/${slug}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        toast.error(res.status === 403 ? "You do not have access to this guide" : "Failed to load guide");
        setGuide(null);
      } else {
        setGuide(await res.json());
      }
    } catch (e) {
      toast.error("Failed to load guide");
    }
    setLoading(false);
  }, [slug, token]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const expandAll = () => setExpanded(new Set((guide?.sections || []).map(s => s.id)));
  const collapseAll = () => setExpanded(new Set());

  const scrollTo = (sectionId, subId) => {
    setExpanded(prev => new Set([...prev, sectionId]));
    setTimeout(() => {
      const sel = subId
        ? `#${CSS.escape(subId)}`
        : `[data-testid="guide-section-${sectionId}"]`;
      const el = document.querySelector(sel);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/guides/${slug}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error(res.status === 403 ? "Admins only" : "Download failed");
        setDownloading(false);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `siwon-harmony-${slug}-guide.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Guide downloaded");
    } catch (e) {
      toast.error("Download failed");
    }
    setDownloading(false);
  };

  if (loading) return <div className="sacred-card text-chakra-crown/60 text-sm">Loading guide…</div>;
  if (!guide)  return <div className="sacred-card text-chakra-crown/60 text-sm">Guide not available.</div>;

  return (
    <div className="space-y-6" data-testid={`guide-viewer-${slug}`}>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} className="text-divine-gold hover:bg-divine-gold/10" data-testid="guide-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex items-center gap-2">
          {canDownload && (
            <Button size="sm" onClick={downloadPdf} disabled={downloading}
              className="bg-divine-gold text-cosmic-void hover:bg-divine-gold/90 text-xs"
              data-testid="guide-download-btn">
              {downloading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
              Download PDF
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowIndex(!showIndex)} className="border-divine-gold/20 text-divine-gold/70 hover:bg-divine-gold/10 text-xs" data-testid="guide-toggle-index">
            <List className="w-3 h-3 mr-1" /> {showIndex ? "Hide" : "Show"} Index
          </Button>
          <Button variant="outline" size="sm" onClick={expandAll} className="border-divine-gold/20 text-divine-gold/70 hover:bg-divine-gold/10 text-xs" data-testid="guide-expand-all">
            Expand All
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll} className="border-divine-gold/20 text-divine-gold/70 hover:bg-divine-gold/10 text-xs" data-testid="guide-collapse-all">
            Collapse All
          </Button>
        </div>
      </div>

      {/* Title */}
      <div className="sacred-card text-center py-6">
        <BookOpen className="w-8 h-8 text-divine-gold mx-auto mb-3" />
        <h1 className="font-heading text-2xl md:text-3xl text-divine-gold">{guide.title}</h1>
        <p className="text-chakra-crown/50 mt-2 text-sm">{guide.subtitle}</p>
      </div>

      <div className={showIndex ? "grid grid-cols-1 lg:grid-cols-4 gap-6" : ""}>
        {/* Index */}
        <AnimatePresence>
          {showIndex && (
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="lg:col-span-1">
              <div className="sacred-card sticky top-24 space-y-3 max-h-[80vh] overflow-y-auto" data-testid="guide-index">
                <h3 className="font-heading text-sm text-divine-gold mb-3 uppercase tracking-wider">Table of Contents</h3>
                {guide.sections.map((sec, si) => (
                  <div key={sec.id}>
                    <button onClick={() => scrollTo(sec.id)} className="text-sm text-chakra-crown hover:text-divine-gold transition-colors text-left w-full font-heading" data-testid={`index-${sec.id}`}>
                      {si + 1}. {sec.title}
                    </button>
                    <div className="ml-4 mt-1 space-y-0.5">
                      {(sec.subsections || []).map((sub, idx) => (
                        <button key={sub.id} onClick={() => scrollTo(sec.id, sub.id)} className="block text-xs text-chakra-crown/50 hover:text-divine-gold/80 text-left w-full truncate">
                          {si + 1}.{idx + 1} {sub.title}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Content */}
        <div className={showIndex ? "lg:col-span-3 space-y-4" : "space-y-4"}>
          {guide.sections.map(section => {
            const isOpen = expanded.has(section.id);
            return (
              <div key={section.id} className="border border-divine-gold/10 rounded-xl overflow-hidden" data-testid={`guide-section-${section.id}`}>
                <button onClick={() => toggle(section.id)} className="w-full flex items-center justify-between p-4 bg-cosmic-light/5 hover:bg-cosmic-light/10 transition-colors text-left" data-testid={`guide-toggle-${section.id}`}>
                  <h2 className="font-heading text-lg text-divine-gold">{section.title}</h2>
                  {isOpen ? <ChevronUp className="w-5 h-5 text-divine-gold/60" /> : <ChevronDown className="w-5 h-5 text-divine-gold/60" />}
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
                      <div className="p-4 space-y-5">
                        {(section.subsections || []).map(sub => (
                          <div key={sub.id} id={sub.id} className="scroll-mt-24" data-testid={`guide-sub-${sub.id}`}>
                            <h3 className="font-heading text-base text-chakra-crown mb-2">{sub.title}</h3>
                            <ul className="space-y-2">
                              {(sub.content || []).map((line, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-chakra-crown/70 leading-relaxed">
                                  <ChevronRight className="w-4 h-4 text-divine-gold/40 mt-0.5 shrink-0" />
                                  <span>{line}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
